import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import BodyText from 'components/widget/BodyText';
import { FullWidthPageContent } from 'layouts/SingleColumnLayout';
import UsageAndPrice from 'components/UsageAndPrice';
import { FullWidthPaperContent } from 'components/Paper';
import Spinner from 'components/Spinner';
import WidgetContainer from 'components/widget/WidgetContainer';
import moment from 'moment';
import Button from 'components/Button';
import { TooltipTextWrapper } from 'components/Tooltip';
import config from 'Config';
import InfoButton from 'components/InfoButton';
import Checkbox from 'components/Checkbox';

const Wrapper = styled.div`
  .topSection {
    margin-bottom: ${({ theme }) => theme.spacing * 3}px;
  }

  .formCta {
    padding: ${({ theme }) => theme.spacing * 2}px;
    > * {
      margin-bottom: ${({ theme }) => theme.spacing * 2}px;
    }
  }
  .Checkbox {
    .checkboxUi{
      margin: 0 1em 0 0;
    }
    p {
      margin-top: 0;
    }
  }
`;

const PlanReview = styled(FullWidthPaperContent)`
  min-height: 100px;
  position: relative;

  .newPlan {
    h2 {
      margin: 0 0 ${({ theme }) => theme.spacing}px;
    }
  }
`;

const ChangeLensReviewView = ({
  className,
  selectedPlan,
  nextPack,
  onConfirm,
  onCancel,
  nextUnpaidPack,
  solutionPrice,
}) => {
  const classes = ['ChangeLensReviewView'];
  const [clinicalConfirmation, setClinicalConfirmation] = useState(false);

  const defaultMsg = 'Because your contact lenses are currently being dispatched, we\'ll change your package from the following delivery.';
  const pendingMsg = (
    <>
      Because you’ve already made payment(s) towards your next
      delivery we’re unable to change it online. This change will
      now affect your subsequent delivery. Call&nbsp;
      <a href={`tel:${config.customerServicePhoneNumber}`}>
        {config.customerServicePhoneNumber}
      </a>
      .

    </>
  );

  if (className) classes.push(className);
  return (
    <Wrapper className={classes.join(' ')}>
      <div className="topSection">
        <h1 className="no_margin_top">You are nearly done...</h1>
        <BodyText>
          Your new easycare package and monthly payment will be changed to
        </BodyText>
      </div>
      <FullWidthPageContent>
        <WidgetContainer noRoundedCorners>
          <PlanReview className="section">
            {!selectedPlan && <Spinner relativeToParent />}
            {selectedPlan && (
              <div className="newPlan">
                <h4>Your new easycare package</h4>
                <UsageAndPrice
                  usage={selectedPlan.title}
                  price={{
                    currency: selectedPlan.price.currency,
                    price: selectedPlan.price.price + solutionPrice,
                    frequency: selectedPlan.price.frequency,
                    small: true,
                  }}
                />
                <BodyText>
                  {selectedPlan.subTitle}
                  {solutionPrice ? ' with solution' : ''}
                </BodyText>
              </div>
            )}
          </PlanReview>
          <FullWidthPaperContent className="section">
            <h4>Effective from</h4>
            <BodyText>
              Your delivery in&nbsp;
              {moment(nextUnpaidPack.delivery_date).format('MMMM YYYY')}
            </BodyText>
          </FullWidthPaperContent>

          {nextPack !== nextUnpaidPack && (
            <InfoButton
              label={`Why doesn't this affect my ${moment(nextPack.delivery_date).format('MMMM YYYY')} delivery?`}
              altStyle
            >
              <TooltipTextWrapper>
                {nextPack.status === 'Pending' ? pendingMsg : defaultMsg}
              </TooltipTextWrapper>
            </InfoButton>
          )}
          <FullWidthPaperContent className="section">
            <h4>About your eye health</h4>
            <Checkbox onChange={(e) => setClinicalConfirmation(e.target.checked)}>
              <p>
                I confirm I am not experiencing any problems with my vision,
                eye health or contact lenses including discomfort or redness.
              </p>
              <p>Please contact your local store if you have any concerns.</p>
            </Checkbox>
          </FullWidthPaperContent>
        </WidgetContainer>

        <div className="formCta">
          <Button
            fullWidth
            type="button"
            onClick={onConfirm}
            disabled={!clinicalConfirmation}
            dataLayer={{ action: 'Change Lens Amount', label: `Confirm Change - ${selectedPlan.title}` }}
          >
            Continue and save changes
          </Button>
          <Button inverse fullWidth type="button" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </FullWidthPageContent>
    </Wrapper>
  );
};

ChangeLensReviewView.displayName = 'ChangeLensReviewView';

ChangeLensReviewView.propTypes = {
  className: PropTypes.string,
  nextPack: PropTypes.shape({
    delivery_date: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
  }).isRequired,
  nextUnpaidPack: PropTypes.shape({
    delivery_date: PropTypes.string.isRequired,
  }).isRequired,
  selectedPlan: PropTypes.shape({
    title: PropTypes.string.isRequired,
    price: PropTypes.shape({
      currency: PropTypes.string.isRequired,
      price: PropTypes.number.isRequired,
      frequency: PropTypes.string.isRequired,
    }).isRequired,
    subTitle: PropTypes.string.isRequired,
  }).isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  solutionPrice: PropTypes.number,
};

ChangeLensReviewView.defaultProps = {
  className: null,
  solutionPrice: 0,
};

export default ChangeLensReviewView;
